import React, {
  useState,
  useEffect,
  useRef,
  ReactElement,
  AriaAttributes,
  useCallback
} from "react";
import styles from "./DropDown.module.scss";
import clsx from "clsx";
import { Icon } from "../Icon/Icon";
import { Button } from "../Button/Button";
import { stringIsPresent } from "../utils/checks";

type ExpandType = "inline" | "absolute" | "open";
type Size = "default" | "big";

export interface DropDownProps extends AriaAttributes {
  id?: string;
  label: string | ReactElement;
  selectedValuesLabel?: string | ReactElement;
  variant?: "default" | "transparent";
  size?: Size;
  closeOnClickOutside?: boolean;
  disabled?: boolean;
  closeButtonLabel?: string;
  role?: string;

  //style
  className?: string;
  buttonClassName?: string;
  contentClassName?: string;
  contentWrapperClassName?: string;
  iconClassName?: string;
  showIcon?: boolean;
  expandOnMobile?: ExpandType;
  expandOnTablet?: ExpandType;
  expandOnDesktop?: ExpandType;
  dataContentPiece?: string;
  dataContentName?: string;
}

export const DropDown: React.FC<React.PropsWithChildren<DropDownProps>> = ({
  id,
  label,
  children,
  className,
  buttonClassName,
  iconClassName,
  contentClassName,
  contentWrapperClassName,
  variant = "default",
  size = "default",
  showIcon = true,
  closeOnClickOutside = false,
  disabled = false,
  expandOnDesktop = "absolute",
  expandOnMobile = "inline",
  expandOnTablet = "inline",
  dataContentName,
  dataContentPiece,
  closeButtonLabel,
  selectedValuesLabel,
  ...props
}) => {
  const withSelectedValuesLabel =
    typeof selectedValuesLabel == "string"
      ? stringIsPresent(selectedValuesLabel)
      : React.Children.count(selectedValuesLabel) > 0;

  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const handleClickOutside = useCallback(
    (e) => {
      if (
        closeOnClickOutside &&
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    },
    [closeOnClickOutside]
  );

  // close on click outside
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-content-name={dataContentName}
      ref={containerRef}
      className={clsx(styles.dropDown, className, {
        [styles.dropDownDefault]: variant == "default",
        [styles.dropDownTransparent]: variant == "transparent",
        [styles.dropDownSizeBig]: size == "big",
        [styles.mobileExpandInline]: expandOnMobile == "inline",
        [styles.mobileExpandAbsolute]: expandOnMobile == "absolute",
        [styles.mobileExpandOpen]: expandOnMobile == "open",
        [styles.tabletExpandInline]: expandOnTablet == "inline",
        [styles.tabletExpandAbsolute]: expandOnTablet == "absolute",
        [styles.tabletExpandOpen]: expandOnTablet == "open",
        [styles.desktopExpandInline]: expandOnDesktop == "inline",
        [styles.desktopExpandAbsolute]: expandOnDesktop == "absolute",
        [styles.desktopExpandOpen]: expandOnDesktop == "open",
        [styles.dropDownOpen]: isOpen
      })}
    >
      <button
        {...props}
        id={id}
        className={clsx(styles.button, buttonClassName, {
          [styles.buttonWithSelection]: withSelectedValuesLabel
        })}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={label?.toString()}
        data-content-piece={dataContentPiece}
        disabled={disabled}
        aria-expanded={isOpen}
      >
        <span
          className={clsx(styles.labelsContainer, {
            [styles.withSelection]: withSelectedValuesLabel
          })}
        >
          <span className={styles.label}>{label}</span>
          {withSelectedValuesLabel && (
            <span className={styles.valuesLabel}>{selectedValuesLabel}</span>
          )}
        </span>
        {showIcon && (
          <Icon
            name="chevron-down"
            size="1.5x"
            padding={false}
            className={clsx(iconClassName, styles.icon)}
            ariaHidden={true}
          />
        )}
      </button>
      <div
        tabIndex={-1}
        className={clsx(styles.contentWrapper, contentWrapperClassName)}
        aria-hidden={!isOpen}
      >
        <div
          className={clsx(styles.content, contentClassName)}
          data-content-name={
            dataContentName ? `${dataContentName}-content` : undefined
          }
          tabIndex={-1}
        >
          {children}
        </div>
        {closeButtonLabel && (
          <div className={styles.close}>
            <Button
              variant="text"
              renderAs="button"
              onClick={() => setIsOpen(false)}
              dataContentPiece="close"
            >
              {closeButtonLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
